import type { ReactElement, ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

/**
 * Mount once, near the root. Shares one open-delay across every tooltip, so
 * moving along a row of icon buttons shows each label at once instead of
 * waiting out the delay again for every one.
 */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <TooltipPrimitive.Provider delayDuration={300} skipDelayDuration={200}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

export interface TooltipProps {
  /** The label. */
  content: ReactNode;
  /** Exactly one element that can take a ref and focus — a button or a link. */
  children: ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Gap between the trigger and the label, in px. */
  sideOffset?: number;
  /**
   * Keep the label from showing — while the control's own menu is open, say.
   * The trigger stays mounted exactly as it was, so its ref and its focus are
   * undisturbed; only the label is withheld.
   */
  disabled?: boolean;
  className?: string;
}

/**
 * A label for a control that shows only an icon.
 *
 * Opens on hover AND on keyboard focus, closes on Escape, and stays inside the
 * viewport — the three things a `title` attribute does not do. A tooltip names
 * a control; it is not a place for anything a user must read to proceed.
 *
 * Needs a `<TooltipProvider>` above it (the app shell supplies one).
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  sideOffset = 6,
  disabled = false,
  className,
}: TooltipProps) {
  const withheld = disabled || !content;

  return (
    // `open={false}` while withheld; `undefined` otherwise hands control back
    // to Radix. The tree is the same either way, so toggling never remounts
    // the trigger.
    <TooltipPrimitive.Root open={withheld ? false : undefined}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={8}
          className={cn(
            'z-[110] max-w-xs select-none rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-elevated',
            'dark:bg-gray-100 dark:text-gray-900',
            'animate-fade-in-plain',
            className
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
